/* eslint-disable react/prop-types */
import React from 'react';
import classNames from 'classnames';
import Tooltip from '@tencent/dui/lib/components/Tooltip';

import Styles from './index.less';
import SectionTitleInput from './section-title-input';
import { IHistoryManager } from '@src/slide/application/service/history-manager/core/history-manager.interface';
import { hexToRgba } from '@src/slide/infrastructure/module/independent/color/transform';

interface ISectionTitleProps {
    name: string;
    numSlides: number;
    setSectionName: (name: string) => void;
    sectionIndex: number;
    isFolded: boolean;
    toggleFolded: () => void;
    isSelected: boolean;
    setSelected: () => void;
    selectSection: () => void;
    nameEditingIndex: number;
    setIsEditingName: (isEditingName: boolean) => void;
    showContextMenu: (e: MouseEvent) => void;
    hideContextMenu: () => void;
    mousedown: (event: MouseEvent, sectionIndex: number) => void;
    isDraggingSection: boolean;
    isDragHovering: boolean;
    historyManager?: IHistoryManager;
    canEdit: boolean;
}

type SectionTitleStaticProps = {
    SectionTitleHeight: number;
};

const SectionTitle: React.FC<ISectionTitleProps> & SectionTitleStaticProps = (props) => {
    const parentStyle = classNames({
        [Styles['slide-list-section-title']]: true,
        [Styles['slide-list-section-title-selected']]: props.isSelected,
        [Styles['slide-list-section-title-dragging']]: props.isSelected && props.isDraggingSection,
        [Styles['slide-list-section-title-drag-hovering']]: props.isDragHovering && props.isFolded && !props.isDraggingSection,
        'slide-section-title': true,
    });
    const arrowIconFoldStyle = Styles[`slide-list-section-title-icon-${props.isFolded ? 'folded' : 'unfolded'}`];
    const arrowIconStyle = classNames({
        [arrowIconFoldStyle]: true,
        [Styles['slide-list-section-title-icon']]: true,
    });

    const isTooltipEvent = (event: MouseEvent): boolean => (event.target as HTMLElement).className.includes('tooltip');

    const onSectionTitleMouseDown: React.MouseEventHandler<HTMLDivElement> = (event) => {
        if (isTooltipEvent(event.nativeEvent)) {
            return;
        }
        if (props.nameEditingIndex !== -1) {
            return;
        }
        props.selectSection();
        props.setSelected();
        props.hideContextMenu();
        props.mousedown(event.nativeEvent, props.sectionIndex);
    };
    const onSectionTitleContextMenu: React.MouseEventHandler<HTMLDivElement> = (event) => {
        if (isTooltipEvent(event.nativeEvent)) {
            return;
        }
        onSectionTitleMouseDown(event);
        props.showContextMenu(event.nativeEvent);
    };
    const onSectionTitleDoubleClick: React.MouseEventHandler<HTMLDivElement> = (event) => {
        if (isTooltipEvent(event.nativeEvent) || !props.canEdit) {
            return;
        }
        props.setIsEditingName(true);
    };
    const onInputBlur = () => {
        props.setIsEditingName(false);
    };
    const getBackgroundColor = () => {
        const { sectionIndex } = props;
        let bgColor = '';
        // 修订记录：根据修改作者调整背景色
        if (props.historyManager?.isShowingHistory) {
            const { sectionModification } = props.historyManager;
            const authors = sectionModification?.[sectionIndex];
            if (authors) {
                const id = authors[authors.length - 1];
                if (id) {
                    const hexColor = props.historyManager.getColorForId(id);
                    bgColor = hexToRgba(hexColor, 0.2);
                }
            }
        }
        return bgColor;
    };

    const isEditingName = props.nameEditingIndex === props.sectionIndex;
    return (
        <div
            className={parentStyle}
            onContextMenu={onSectionTitleContextMenu}
            onMouseDown={onSectionTitleMouseDown}
            onDoubleClick={onSectionTitleDoubleClick}
            style={{
                backgroundColor: getBackgroundColor(),
            }}
        >
            <span
                // 双击事件阻止冒泡，避免双击小箭头触发编辑节名称
                onDoubleClick={event => event.stopPropagation()}
                className={arrowIconStyle}
                onClick={props.toggleFolded}
            />
            <span style={{ display: 'inline-block', width: 5 }}></span>
            {isEditingName ? (
                <SectionTitleInput
                    setName={props.setSectionName}
                    isEditingName={isEditingName}
                    onInputBlur={onInputBlur}
                    name={props.name}
                />
            ) : (
                <Tooltip
                    title={props.name}
                    trigger="hover"
                >
                    <div className={Styles['slide-list-section-title-name-container']}>
                        <span className={Styles['slide-list-section-title-name']}>{props.name}</span>
                        {props.isFolded && <span className={Styles['slide-list-section-title-num-slides']}>{`(${props.numSlides})`}</span>}
                    </div>
                </Tooltip>
            )}
        </div>
    );
};

// 字号为 8px，section-title 的高度
SectionTitle.SectionTitleHeight = 24.5;

export default SectionTitle;
